import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, SectionMargins, CardsSectionContent, CardItemConfig } from "api/shop";
import { HeadingTagSelect, PaziaFormButton, PaziaInput, PaziaSelect } from "components/atoms";
import { Select, IDropdownOption } from "components/atoms/PaziaSelect/PaziaSelect";
import { PaziaQuill } from "components/atoms/PaziaQuill/PaziaQuill";
import { useCourseGroupAttributesAll } from "api/coursegroup-attributes";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

const cardSchema = yup.object({
  header: yup.string().default(""),
  headerTag: yup.string(),
  content: yup.string().default(""),
  bgColor: yup.string().required("Kolor tła jest wymagany").default("#f1f5f9"),
  textColor: yup.string().required("Kolor tekstu jest wymagany").default("#1e293b"),
  attributeId: yup.number().optional().nullable(),
});

const schema = yup
  .object({
    animation: yup.string().required("Animacja jest wymagana"),
    headerText: yup.string().default(""),
    headerTag: yup.string(),
    cards: yup.array().of(cardSchema).min(1, "Dodaj przynajmniej jedną kartę"),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormCardItem {
  header: string;
  headerTag?: string;
  content: string;
  bgColor: string;
  textColor: string;
  attributeId?: number | null;
}

interface FormValues {
  animation: string;
  headerText: string;
  headerTag: string;
  cards: FormCardItem[];
  margins: SectionMargins;
}

export interface CardsSectionFormValues {
  animation: string;
  header?: { text: string; tag?: string };
  cards: CardItemConfig[];
  margins?: SectionMargins;
}

interface CardsSectionFormProps {
  onSubmit: (data: CardsSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: CardsSectionContent;
  isLoading?: boolean;
}

export const CardsSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: CardsSectionFormProps) => {
  const { allAttributes, isLoading: loadingAttributes } = useCourseGroupAttributesAll();

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      animation: defaultValues?.animation ?? "fade-bottom",
      headerText: defaultValues?.header?.text ?? "",
      headerTag: defaultValues?.header?.tag ?? "h2",
      cards:
        defaultValues?.cards?.map((c) => ({
          header: c.header,
          headerTag: c.headerTag ?? "h3",
          content: c.content,
          bgColor: c.bgColor,
          textColor: c.textColor,
          attributeId: c.attributeId ?? null,
        })) ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });

  const { handleSubmit, control } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "cards",
  });

  const handleFormSubmit = (data: FormValues) => {
    const formatted: CardsSectionFormValues = {
      animation: data.animation,
      header: data.headerText ? { text: data.headerText, tag: data.headerTag as any } : undefined,
      cards: data.cards.map((c) => ({
        header: c.header,
        headerTag: c.headerTag as any,
        content: c.content,
        bgColor: c.bgColor,
        textColor: c.textColor,
        ...(c.attributeId ? { attributeId: c.attributeId } : {}),
      })),
      margins: data.margins,
    };
    onSubmit(formatted);
  };

  const updateCard = (index: number, field: keyof FormCardItem, value: string | number | undefined | null) => {
    methods.setValue(`cards.${index}.${field}` as any, value);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <div className="grid gap-3 md:grid-cols-2">
          <PaziaSelect name="animation" label="Animacja" required options={ANIMATION_OPTIONS} />
          <PaziaInput name="headerText" label="Nagłówek" />
        </div>

        <HeadingTagSelect name="headerTag" />

        {/* Cards */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Karty</h4>
            <button
              type="button"
              onClick={() => append({ header: "", headerTag: "h3", content: "", bgColor: "#f1f5f9", textColor: "#1e293b", attributeId: null })}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj kartę
            </button>
          </div>

          {fields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak kart. Kliknij &quot;Dodaj kartę&quot;, aby dodać.
            </p>
          ) : (
            <div className="space-y-4">
              {fields.map((field, index) => {
                const card = methods.watch(`cards.${index}`);
                const isAttribute = !!card?.attributeId;

                return (
                  <div
                    key={field.id}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Karta {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                        title="Usuń kartę"
                      >
                        <MdDelete size={18} />
                      </button>
                    </div>
                    <div className="grid gap-3">
                      {/* Mode selector */}
                      <div>
                        <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Tryb</label>
                        <select
                          value={isAttribute ? "attribute" : "static"}
                          onChange={(e) => {
                            if (e.target.value === "static") {
                              updateCard(index, "attributeId", null);
                            } else {
                              updateCard(index, "attributeId", allAttributes[0]?.id ?? -1);
                            }
                          }}
                          className="w-full rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200"
                        >
                          <option value="static">Statyczna</option>
                          <option value="attribute">Atrybut</option>
                        </select>
                      </div>

                      {/* Attribute selector */}
                      {isAttribute && (
                        <div>
                          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Atrybut</label>
                          <Select
                            name={`card-attr-${index}`}
                            options={allAttributes.map((a) => ({ value: a.id, label: a.name }))}
                            value={card.attributeId ?? ""}
                            isClearable={false}
                            isLoading={loadingAttributes}
                            onChange={(opt: IDropdownOption | null) => {
                              updateCard(index, "attributeId", opt ? Number(opt.value) : null);
                            }}
                          />
                        </div>
                      )}

                      <PaziaInput
                        name={`cards.${index}.header`}
                        label={isAttribute ? "Nagłówek (opcjonalny, domyślnie nazwa atrybutu)" : "Nagłówek"}
                        required={!isAttribute}
                      />
                      <HeadingTagSelect name={`cards.${index}.headerTag`} label="Tag nagłówka karty" />

                      <div className="flex gap-3">
                        <div>
                          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Kolor tła</label>
                          <input
                            type="color"
                            {...methods.register(`cards.${index}.bgColor`)}
                            className="h-8 w-12 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Kolor tekstu</label>
                          <input
                            type="color"
                            {...methods.register(`cards.${index}.textColor`)}
                            className="h-8 w-12 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
                          />
                        </div>
                      </div>

                      {/* Content editor - only for static mode */}
                      {!isAttribute && (
                        <div>
                          <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">Treść</label>
                          <PaziaQuill
                            value={methods.watch(`cards.${index}.content`)}
                            onChange={(html) => methods.setValue(`cards.${index}.content`, html)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};
